import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Facebook, Twitter, Linkedin, Link2 } from "lucide-react";
import { toast } from "sonner";
import { mediaUrl } from "@/lib/api";
import { formatDate } from "@/lib/utils-format";
import ArticleCard from "@/components/public/ArticleCard";
import AdSlot from "@/components/public/AdSlot";

export default function ArticleView({ article, preview = false, ads = true }) {
  if (!article) return null;

  const share = (net) => {
    const url = window.location.href;
    const t = encodeURIComponent(article?.title || "");
    const map = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${t}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    };
    if (net === "copy") { navigator.clipboard.writeText(url); toast.success("Enlace copiado"); return; }
    window.open(map[net], "_blank");
  };

  const dateStr = article.published_at || article.scheduled_at || article.created_at;

  return (
    <article data-testid="article-view">
      <div className="relative h-[70vh] min-h-[500px] w-full overflow-hidden bg-sunset-ink">
        {article.cover_image ? (
          <motion.img initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 1.4, ease: "easeOut" }}
            src={mediaUrl(article.cover_image)} alt={article.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-sunset-ink to-[#2b2320]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute inset-0 max-w-[900px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-16">
          {article.category && (
            <Link to={`/categoria/${article.category.slug}`} className="kicker text-white/90 mb-5">{article.category.name}</Link>
          )}
          <h1 className="font-serif text-white text-4xl md:text-6xl leading-[1.02] tracking-tight max-w-3xl">{article.title}</h1>
        </div>
      </div>

      <div className="max-w-[720px] mx-auto px-5 md:px-0 py-14">
        {article.excerpt && <p className="font-serif text-2xl md:text-3xl leading-snug text-sunset-ink mb-10 italic">{article.excerpt}</p>}

        <div className="flex items-center justify-between flex-wrap gap-4 pb-8 mb-10 border-b border-sunset-border">
          <div className="flex items-center gap-4">
            {article.author && (
              <Link to={`/autor/${article.author.id}`} className="flex items-center gap-3 group">
                {article.author.photo && <img src={mediaUrl(article.author.photo)} alt="" className="w-11 h-11 rounded-full object-cover" />}
                <div>
                  <p className="text-sm font-medium group-hover:text-sunset-accent transition-colors">{article.author.name}</p>
                  <p className="text-xs text-sunset-muted">{formatDate(dateStr)} · {article.reading_time} min</p>
                </div>
              </Link>
            )}
          </div>
          {!preview && (
            <div className="flex items-center gap-3 text-sunset-muted">
              <button onClick={() => share("facebook")} aria-label="Facebook" className="hover:text-sunset-accent transition-colors"><Facebook size={18} /></button>
              <button onClick={() => share("twitter")} aria-label="Twitter" className="hover:text-sunset-accent transition-colors"><Twitter size={18} /></button>
              <button onClick={() => share("linkedin")} aria-label="LinkedIn" className="hover:text-sunset-accent transition-colors"><Linkedin size={18} /></button>
              <button onClick={() => share("copy")} data-testid="share-copy" aria-label="Copiar" className="hover:text-sunset-accent transition-colors"><Link2 size={18} /></button>
            </div>
          )}
        </div>

        <div className="article-body" dangerouslySetInnerHTML={{ __html: article.content || "" }} />

        {article.gallery?.length > 0 && (
          <div className="grid grid-cols-2 gap-3 my-12">
            {article.gallery.map((g, i) => (
              <img key={i} src={mediaUrl(g)} alt="" loading="lazy" className="w-full aspect-square object-cover" />
            ))}
          </div>
        )}
      </div>

      {ads && <div className="max-w-[720px] mx-auto px-5"><AdSlot position="In-Article" className="mb-12" /></div>}

      {article.related?.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 border-t border-sunset-border">
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-12">Artículos Relacionados</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
            {article.related.map((a, i) => <ArticleCard key={a.id} article={a} size="md" index={i} />)}
          </div>
        </section>
      )}
    </article>
  );
}
